import React, { useCallback, useContext, useEffect, useRef, VFC } from 'react';

import classNames from 'classnames';
import { kebabCase } from 'lodash';
import mgl from 'mintel-graphing-library';
import { Text } from 'mintel-react-ui';
import LazyLoad from 'react-lazy-load';

import {
  ChartContentJson,
  ContentContainerJson,
  ImageContentJson,
  MediaContainerJson,
  Present,
  PresentOption,
  SocialMediaContentJson,
} from '/types';
import { Asset, assetUri } from '/utils/assetUri';
import {
  BlockBackgroundContext,
  BlockMetadataContext,
  MetadataContext,
  PresentModeContext,
  PublishStatusContext,
  UserAccessRestrictionContext,
} from '/utils/contexts';
import { cleanLabels, contentFilter, stripTags } from '/utils/functions';
import useTranslate from '/utils/translations';

import styles from './MediaColumn.scss';
import SocialMediaColumn from './SocialMediaColumn';

const DEFAULT_CHART_HEIGHT = 533;

// We want different image sizes on different screen-sizes/resolutions, but
// we still want them to be statically sized to avoid layout shift.
// So we provide width, aspect ratio and set `max-width: 100%` in styles.
export const IMAGE_SIZE = {
  SMALL: { width: 300, aspect: '1' },
  MEDIUM: { width: 800, aspect: '3/2' },
  LARGE: { width: 1067, aspect: '2' },
  FULL: { width: 1600, aspect: '16/9' },
};

type ImageColumnProps = {
  source?: string;
  link?: string;
  alt?: string;
  imageSize?: { width: number; aspect: string };
  hide?: boolean;
};

export const ImageColumn: VFC<ImageColumnProps> = ({
  source = '',
  alt = '',
  link = '',
  imageSize = IMAGE_SIZE.SMALL,
  hide = false,
}) => {
  const publishStatus = useContext(PublishStatusContext);
  const imgUrl = source.startsWith('workspace_SpacesStore')
    ? assetUri(source, Asset.Image, publishStatus)
    : source;

  const Image: VFC = () => (
    <img
      src={imgUrl}
      alt={alt}
      className={classNames(styles['image'], {
        [styles.hide]: hide,
      })}
      style={{
        aspectRatio: imageSize.aspect,
        maxWidth: `min(${imageSize.width}px, 100%)`,
      }}
      width={imageSize.width}
    />
  );

  return link ? (
    <a href={link} className={styles.imageLink}>
      <Image />
    </a>
  ) : (
    <Image />
  );
};

/**
 * This is an UglyHack™ to scale down overflowing radar charts.
 * Radar charts are fixed size and unresponsive. To fit them into their container
 * either the container has to give up and resize, or the chart should scale down.
 * This function does the latter.
 */
const scaleFixedSizeChart = (
  element: HTMLDivElement,
  specWidth: number,
): void => {
  const chart = element.getElementsByClassName(
    'highcharts-container',
  )[0] as HTMLDivElement;

  const ratio = element.offsetWidth / specWidth;
  if (ratio < 1) {
    chart.style.transform = `scale(${ratio})`;
    chart.style.transformOrigin = '0';
  } else {
    chart.style.margin = 'auto';
  }
};

const formatContentContainerForCsv = (
  container: Pick<ContentContainerJson, 'elements'> | undefined,
): string => {
  if (container === undefined) {
    return '';
  }

  const { prefix, content } = container.elements;
  const lines = [stripTags(prefix?.content), stripTags(content?.content)];
  return lines.filter((v) => !!v).join(' ');
};

type ChartColumnProps = Pick<ChartContentJson, 'spec'> & {
  height?: number;
  presentMode?: Present;
};

const ChartColumn: VFC<ChartColumnProps> = ({
  spec,
  height = DEFAULT_CHART_HEIGHT,
  presentMode = Present.Off,
}) => {
  const t = useTranslate();
  const { language, slug: reportSlug } = useContext(MetadataContext);
  const { base, source, title: blockTitle, shareLink } = useContext(
    BlockMetadataContext,
  );
  const chartRef = useRef<HTMLDivElement>(null);
  const accessRestrictions = useContext(UserAccessRestrictionContext);

  const specString = JSON.stringify(spec);

  const refreshChart = useCallback((): void => {
    const specClone = JSON.parse(specString);
    specClone.options.exportingEnabled =
      !presentMode && !!accessRestrictions.downloads?.chartData;
    if (!specClone.options.height) {
      specClone.options.height = height;
    }

    specClone.options.exportFilename = `Mintel_${reportSlug}`;
    if (blockTitle !== undefined) {
      specClone.options.exportFilename += `_${kebabCase(blockTitle.content)}`;
    }

    const axisTitles = [
      specClone.options?.xAxis?.title?.text,
      specClone.options?.yAxis?.title?.text,
    ].filter((v) => !!v);
    specClone.options.footerNote = [
      ...axisTitles.map((figure) => t('Figures shown: {{figure}}', { figure })),
      formatContentContainerForCsv(base),
      formatContentContainerForCsv(source),
      t('URL: {{link}}', { link: shareLink }),
    ].filter((v) => !!v);

    if (presentMode) {
      cleanLabels(specClone);
    }

    mgl.api.drawChart(chartRef.current, specClone);
    if (chartRef.current && specClone.options.width) {
      scaleFixedSizeChart(chartRef.current, specClone.options.width);
    }
  }, [
    t,
    base,
    blockTitle,
    reportSlug,
    shareLink,
    source,
    height,
    specString,
    presentMode,
    accessRestrictions.downloads?.chartData,
  ]);

  useEffect(() => {
    if (chartRef?.current) {
      refreshChart();
    }
  }, [presentMode, chartRef, refreshChart]);

  useEffect(() => {
    mgl.api.setGlobalOptions({ locale: language });
  }, [language]);

  const chartDiv = (
    <div onClick={(e): void => e.stopPropagation()} ref={chartRef} />
  );

  return (
    /* @ts-expect-error Server Component */
    <LazyLoad onContentVisible={refreshChart} height={height}>
      {chartDiv}
    </LazyLoad>
  );
};

type MediaColumnProps = Pick<MediaContainerJson, 'elements'> & {
  imageSize?: { width: number; aspect: string };
  presentOption?: PresentOption;
  hide?: boolean;
};

export const MediaColumn: VFC<MediaColumnProps> = ({
  elements = {},
  imageSize,
  presentOption = PresentOption.Default,
  hide = false,
}) => {
  const presentMode = useContext(PresentModeContext);
  const { darkBg } = useContext(BlockBackgroundContext);
  const caption = contentFilter(elements.caption, presentMode, presentOption);
  const isImage = ['image', 'svg'].includes(elements.image?.type || '');
  const image = elements.image as ImageContentJson;
  const socialMediaEmbed = elements.image as SocialMediaContentJson;

  return (
    <div
      className={classNames(styles.mediaColumn, {
        [styles.hide]: hide,
      })}
    >
      {isImage && (
        <ImageColumn
          alt={image.alt}
          source={image.source}
          link={image.link}
          imageSize={imageSize}
        />
      )}
      {elements.image?.type === 'social-media' && (
        <SocialMediaColumn {...socialMediaEmbed} />
      )}
      {elements.image?.type === 'chart' && (
        <ChartColumn
          spec={elements.image?.spec}
          presentMode={presentMode}
          height={
            elements.image?.spec.options.height || elements.image?.media_height
          }
        />
      )}
      {caption && !elements?.caption?.hidden && (
        <Text.Body darkBg={darkBg}>
          <figcaption
            className={styles.smallCaption}
            dangerouslySetInnerHTML={{
              __html: caption,
            }}
          />
        </Text.Body>
      )}
    </div>
  );
};
